import React, { useState } from "react";
import { Dialog } from "../containers/Dialog.tsx";
import { Exercise } from "../../api/types.ts";
import { Button } from "../widgets/Button.tsx";
import { useDialog } from "../DialogManager.tsx";
import { useMergeExercise } from "../../api/hooks.ts";

interface MergeExerciseDialogProps {
  open: boolean;
  onClose: () => void;
  exercise1: Exercise;
  exercise2: Exercise;
}

export const MergeExerciseDialog: React.FC<MergeExerciseDialogProps> = ({
  open,
  onClose,
  exercise1,
  exercise2,
}) => {
  const { setError } = useDialog();

  const mergeExerciseMutation = useMergeExercise();

  const [selectedTarget, setSelectedTarget] = useState<string>(exercise1.id);

  const handleConfirm = () => {
    const sourceId =
      selectedTarget === exercise1.id ? exercise2.id : exercise1.id;

    mergeExerciseMutation.mutate(
      { targetExerciseId: selectedTarget, sourceExerciseId: sourceId },
      { onSuccess: onClose, onError: (e) => setError(e) },
    );
  };

  return (
    <Dialog open={open} onClose={onClose} title="Merge Exercises">
      <Dialog.Content>
        <p className="mb-4">
          Select which exercise you want to keep. Routines referencing the other
          exercise will be updated to reference the selected one. Workouts
          created with this exercise are however not affected by this merge.
        </p>

        <label
          className={`flex items-center p-3 border-2 rounded-lg cursor-pointer transition-colors ${
            selectedTarget === exercise1.id
              ? "border-primary bg-primary bg-opacity-5"
              : "border-gray-200 hover:border-gray-300"
          }`}
        >
          <input
            type="radio"
            name="targetExercise"
            value={exercise1.id}
            checked={selectedTarget === exercise1.id}
            onChange={(e) => setSelectedTarget(e.target.value)}
            className="mr-3"
          />
          <div>
            <div className="font-medium">{exercise1.name}</div>
            {exercise1.description && (
              <div className="text-sm text-gray-600">
                {exercise1.description}
              </div>
            )}
          </div>
        </label>

        <label
          className={`flex items-center p-3 border-2 rounded-lg cursor-pointer transition-colors ${
            selectedTarget === exercise2.id
              ? "border-primary bg-primary bg-opacity-5"
              : "border-gray-200 hover:border-gray-300"
          }`}
        >
          <input
            type="radio"
            name="targetExercise"
            value={exercise2.id}
            checked={selectedTarget === exercise2.id}
            onChange={(e) => setSelectedTarget(e.target.value)}
            className="mr-3"
          />
          <div>
            <div className="font-medium">{exercise2.name}</div>
            {exercise2.description && (
              <div className="text-sm text-gray-600">
                {exercise2.description}
              </div>
            )}
          </div>
        </label>
      </Dialog.Content>

      <Dialog.Actions>
        <Button
          onClick={handleConfirm}
          disabled={!selectedTarget}
          loading={mergeExerciseMutation.isPending}
          className="px-4 py-2 rounded transition-colors"
          type="submit"
        >
          Merge
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
};
